/** @jsxImportSource @emotion/react */
import {
  AlignItems,
  Box,
  Display,
  Icon,
  IconVariant,
  Space,
} from '@gousto-internal/citrus-react'
import { useCallback, useState } from 'react'
import { useNavigateToSearchPage, useDebouncedSearchArticles } from 'src/hooks'
import { isMobile } from '../../utils/isMobile'
import { SearchBar } from '../SearchBar'
import { SearchResult } from '../SearchResult'

type SearchBoxProps = {
  onSearchMobileToggle: (isOn: boolean) => void,
  isMobileSearchResultVisible: boolean
}

const SearchBox = ({ onSearchMobileToggle, isMobileSearchResultVisible }: SearchBoxProps) => {
  const [query, setQuery] = useState('')
  const [isInputInFocus, setIsInputInFocus] = useState(false)
  const { isLoading, searchResult, error } = useDebouncedSearchArticles(query, 3)
  const isSearchActive = (query && isInputInFocus) || isMobileSearchResultVisible
  const isSearchResultVisible = !isLoading && isSearchActive
  const goToSearch = useNavigateToSearchPage()
  const onShowMore = useCallback(() => goToSearch(query), [goToSearch, query])
  const showCloseButton = isMobileSearchResultVisible && isMobile()

  const onSearchBoxFocus = () => (isMobile() ? onSearchMobileToggle(true) : setIsInputInFocus(true))
  const onSearchBoxBlur = () => !isMobile() && setIsInputInFocus(false)
  const onCloseButtonClick = () => onSearchMobileToggle(false)

  return (
    <Box
      onBlur={onSearchBoxBlur}
      onFocus={onSearchBoxFocus}
      css={{ position: 'relative' }}
      data-testing="search-box"
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        paddingH={[3, 0]}
      >
        {showCloseButton && (
          <>
            <Icon
              name="close"
              variant={IconVariant.Inherit}
              size={5}
              onClick={onCloseButtonClick}
              data-testing="search-close-button"
            />
            <Space direction="horizontal" size={3} />
          </>
        )}
        <SearchBar
          isLoading={isLoading}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Box>
      <SearchResult
        isVisible={isSearchResultVisible}
        searchResult={searchResult}
        error={error}
        onShowMore={onShowMore}
        query={query}
      />
    </Box>
  )
}

export { SearchBox, useNavigateToSearchPage }
